import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Search, Edit2, Trash2, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export default function InvestorsList({ user, onLogout }) {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showTdsDialog, setShowTdsDialog] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    pan_number: '',
    bank_account: '',
    ifsc_code: '',
    password: '',
    tds_applicable: true,
    tds_rate: 10.0
  });

  useEffect(() => {
    fetchInvestors();
  }, []);

  const fetchInvestors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/investors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestors(response.data);
    } catch (error) {
      toast.error('Failed to fetch investors');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvestor = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/investors`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Investor added successfully');
      setShowAddDialog(false);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        pan_number: '',
        bank_account: '',
        ifsc_code: '',
        password: '',
        tds_applicable: true,
        tds_rate: 10.0
      });
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add investor');
    }
  };

  const handleUpdateTds = async (e) => {
    e.preventDefault();
    if (!selectedInvestor) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/investors/${selectedInvestor.investor_id}`, {
        tds_applicable: selectedInvestor.tds_applicable,
        tds_rate: parseFloat(selectedInvestor.tds_rate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('TDS settings updated successfully');
      setShowTdsDialog(false);
      fetchInvestors();
    } catch (error) {
      toast.error('Failed to update TDS settings');
    }
  };

  const handleDeleteInvestor = async (investorId) => {
    if (!window.confirm('Are you sure you want to deactivate this investor?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/investors/${investorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Investor deactivated successfully');
      fetchInvestors();
    } catch (error) {
      toast.error('Failed to deactivate investor');
    }
  };

  const filteredInvestors = investors.filter(inv =>
    inv.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.investor_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout user={user} onLogout={onLogout} currentPage="investors">
      <div className="space-y-6" data-testid="investors-list-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Investors
            </h1>
            <p className="text-muted-foreground text-lg">Manage investor accounts</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <button data-testid="add-investor-button" className="btn-primary flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Add Investor</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Investor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddInvestor} className="space-y-4" data-testid="add-investor-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      data-testid="investor-name-input"
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      data-testid="investor-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <input
                      data-testid="investor-phone-input"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Password *</label>
                    <input
                      data-testid="investor-password-input"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Address *</label>
                    <textarea
                      data-testid="investor-address-input"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="input-field"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">PAN Number</label>
                    <input
                      type="text"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({...formData, pan_number: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">IFSC Code</label>
                    <input
                      type="text"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Bank Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account}
                      onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  {/* TDS Settings */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium mb-3">TDS Settings</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="tds_applicable"
                          checked={formData.tds_applicable}
                          onChange={(e) => setFormData({...formData, tds_applicable: e.target.checked})}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <label htmlFor="tds_applicable" className="text-sm">Apply TDS Deduction</label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">TDS Rate (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="30"
                          value={formData.tds_rate}
                          onChange={(e) => setFormData({...formData, tds_rate: parseFloat(e.target.value) || 10})}
                          className="input-field"
                          disabled={!formData.tds_applicable}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddDialog(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button data-testid="submit-investor-button" type="submit" className="btn-primary">
                    Add Investor
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-investors-input"
                type="text"
                placeholder="Search investors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="investors-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investor ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    TDS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredInvestors.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                      No investors found
                    </td>
                  </tr>
                ) : (
                  filteredInvestors.map((investor) => (
                    <tr key={investor.investor_id} data-testid={`investor-row-${investor.investor_id}`} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{investor.investor_id}</td>
                      <td className="px-6 py-4 font-medium">{investor.full_name}</td>
                      <td className="px-6 py-4 text-sm">{investor.email}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          investor.tds_applicable !== false
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {investor.tds_applicable !== false ? `${investor.tds_rate || 10}%` : 'No TDS'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          investor.status === 'active'
                            ? 'bg-success/10 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {investor.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedInvestor({
                                ...investor,
                                tds_applicable: investor.tds_applicable !== false,
                                tds_rate: investor.tds_rate || 10
                              });
                              setShowTdsDialog(true);
                            }}
                            className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                            title="Edit TDS Settings"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInvestor(investor.investor_id)}
                            className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                            title="Deactivate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* TDS Settings Dialog */}
        <Dialog open={showTdsDialog} onOpenChange={setShowTdsDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit TDS Settings</DialogTitle>
            </DialogHeader>
            {selectedInvestor && (
              <form onSubmit={handleUpdateTds} className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{selectedInvestor.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedInvestor.investor_id}</p>
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="edit_tds_applicable"
                    checked={selectedInvestor.tds_applicable}
                    onChange={(e) => setSelectedInvestor({
                      ...selectedInvestor,
                      tds_applicable: e.target.checked
                    })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="edit_tds_applicable" className="text-sm font-medium">
                    Apply TDS Deduction on Interest Payouts
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">TDS Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="30"
                    value={selectedInvestor.tds_rate}
                    onChange={(e) => setSelectedInvestor({
                      ...selectedInvestor,
                      tds_rate: parseFloat(e.target.value) || 10
                    })}
                    className="input-field"
                    disabled={!selectedInvestor.tds_applicable}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Standard TDS rate is 10% for investors with PAN
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowTdsDialog(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}